import { Box, Button, Text } from "@saleor/macaw-ui-next";
import React from "react";

import { FinanceAnalyticsData } from "../../types";

interface FinanceDashboardProps {
  data: FinanceAnalyticsData;
  currency: string;
  onDownloadReport?: (format: "pdf" | "csv") => void;
  downloadingFormat?: "pdf" | "csv" | null;
}

export const FinanceDashboard: React.FC<FinanceDashboardProps> = ({
  data,
  currency,
  onDownloadReport,
  downloadingFormat,
}) => {
  const formatCurrency = (val: number) =>
    `${currency} ${Number(val || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const kpis = [
    { label: "Other Income (Ledger)", value: formatCurrency(data.totalIncome) },
    { label: "Total Ledger Expenses", value: formatCurrency(data.totalExpenses) },
    { label: "Net P&L Balance", value: formatCurrency(data.netProfitLoss) },
    {
      label: "Canceled / Refunded Value",
      value: `${formatCurrency(data.canceledOrdersAmount)} (${data.canceledOrdersCount} orders)`,
    },
  ];

  return (
    <Box display="flex" flexDirection="column" gap={6}>
      {/* Financial KPIs */}
      <Box display="grid" __gridTemplateColumns="repeat(auto-fit, minmax(220px, 1fr))" gap={4}>
        {kpis.map((kpi, idx) => (
          <Box
            key={idx}
            padding={4}
            borderWidth={1}
            borderStyle="solid"
            borderColor="default1"
            borderRadius={3}
            backgroundColor="default1"
            display="flex"
            flexDirection="column"
            gap={1}
          >
            <Text size={2} color="default1" fontWeight="bold">
              {kpi.label}
            </Text>
            <Text size={6} fontWeight="bold">
              {kpi.value}
            </Text>
          </Box>
        ))}
      </Box>

      {/* Payment Method Distribution */}
      <Box
        borderWidth={1}
        borderStyle="solid"
        borderColor="default1"
        borderRadius={3}
        backgroundColor="default1"
        padding={4}
        display="flex"
        flexDirection="column"
        gap={3}
      >
        <Text size={4} fontWeight="bold">
          Doorstep & Online Payment Method Distribution
        </Text>

        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              textAlign: "left",
              fontSize: "13px",
            }}
          >
            <thead>
              <tr style={{ borderBottom: "1px solid #eee", backgroundColor: "#fafafa" }}>
                <th style={{ padding: "10px" }}>Payment Channel / Method</th>
                <th style={{ padding: "10px", textAlign: "right" }}>Total Amount Processed</th>
              </tr>
            </thead>
            <tbody>
              {data.paymentBreakdown.map((pm, idx) => (
                <tr key={idx} style={{ borderBottom: "1px solid #f0f0f0" }}>
                  <td style={{ padding: "10px", fontWeight: "bold" }}>{pm.method}</td>
                  <td
                    style={{
                      padding: "10px",
                      textAlign: "right",
                      color: "#2e7d32",
                      fontWeight: "bold",
                    }}
                  >
                    {formatCurrency(pm.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Box>

      {/* Chronological Ledger Audit Reconciliation */}
      <Box
        borderWidth={1}
        borderStyle="solid"
        borderColor="default1"
        borderRadius={3}
        backgroundColor="default1"
        padding={4}
        display="flex"
        flexDirection="column"
        gap={3}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
        >
          <Box display="flex" flexDirection="column" gap={1}>
            <Text size={4} fontWeight="bold">
              Chronological Ledger Reconciliation (Daily Cash Flow Audit)
            </Text>
            <Text size={2} color="default1">
              {data.dailyLedger.length} Days Audited
            </Text>
          </Box>
          {onDownloadReport && (
            <Box display="flex" alignItems="center" gap={2}>
              <Button
                variant="secondary"
                size="small"
                disabled={!!downloadingFormat}
                onClick={() => onDownloadReport("pdf")}
              >
                {downloadingFormat === "pdf" ? "PDF..." : "Download Ledger PDF"}
              </Button>
              <Button
                variant="secondary"
                size="small"
                disabled={!!downloadingFormat}
                onClick={() => onDownloadReport("csv")}
              >
                {downloadingFormat === "csv" ? "CSV..." : "Download Ledger CSV"}
              </Button>
            </Box>
          )}
        </Box>

        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              textAlign: "left",
              fontSize: "13px",
            }}
          >
            <thead>
              <tr style={{ borderBottom: "1px solid #eee", backgroundColor: "#fafafa" }}>
                <th style={{ padding: "10px" }}>Date</th>
                <th style={{ padding: "10px", textAlign: "right" }}>Completed Sales</th>
                <th style={{ padding: "10px", textAlign: "right" }}>Other Income</th>
                <th style={{ padding: "10px", textAlign: "right" }}>Store Expenses</th>
                <th style={{ padding: "10px", textAlign: "center" }}>Orders Count</th>
                <th style={{ padding: "10px", textAlign: "right" }}>Net Daily Balance</th>
              </tr>
            </thead>
            <tbody>
              {data.dailyLedger.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ padding: "20px", textAlign: "center", color: "#888" }}>
                    No ledger reconciliation entries found for this range.
                  </td>
                </tr>
              ) : (
                data.dailyLedger.map((row, idx) => (
                  <tr key={idx} style={{ borderBottom: "1px solid #f0f0f0" }}>
                    <td style={{ padding: "10px", fontWeight: "bold" }}>{row.date}</td>
                    <td
                      style={{
                        padding: "10px",
                        textAlign: "right",
                        color: "#2e7d32",
                        fontWeight: "bold",
                      }}
                    >
                      {formatCurrency(row.completedSales)}
                    </td>
                    <td style={{ padding: "10px", textAlign: "right", color: "#1565c0" }}>
                      {formatCurrency(row.income)}
                    </td>
                    <td style={{ padding: "10px", textAlign: "right", color: "#c62828" }}>
                      {formatCurrency(row.expenses)}
                    </td>
                    <td style={{ padding: "10px", textAlign: "center" }}>{row.ordersCount}</td>
                    <td
                      style={{
                        padding: "10px",
                        textAlign: "right",
                        fontWeight: "bold",
                        color: row.netBalance >= 0 ? "#2e7d32" : "#c62828",
                      }}
                    >
                      {formatCurrency(row.netBalance)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Box>
    </Box>
  );
};
